// 담당: 혜원 · 단가표(Rate Card) — 조회 + 편집(추가/수정) 2단 확인 + SF 불러오기
import { useEffect, useState } from 'react'
import {
  Check,
  FileText,
  Info,
  Minus,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  X,
  type LucideIcon,
} from 'lucide-react'
import {
  deleteHubPackage,
  deleteRateProduct,
  fetchRateCard,
  saveHubPackage,
  saveRateProduct,
  seedRateCard,
  type HubPackage,
  type RateCard,
  type RateProduct,
  type RateProductInput,
} from '../creatorsApi'
import youtubeLogo from '../../assets/platform/youtube.svg'
import instagramLogo from '../../assets/platform/instagram.svg'
import tiktokLogo from '../../assets/platform/tiktok.svg'

const won = (n: number) => '₩' + n.toLocaleString('en-US')
let CREATOR_ID = '0012w00000n4rUJAAY' // 셸 라우트(creatorId)로 세팅됨. 없으면 스캐폴드 기본(레오제이).

// 소셜 플랫폼 로고 (asset 관리 — 브랜드 마크. 로고는 디자인 시스템 색 규칙의 예외)
const CHANNEL_LOGO: Record<string, string> = {
  youtube: youtubeLogo,
  instagram: instagramLogo,
  tiktok: tiktokLogo,
}
// 로고 asset 없는 채널(License/Etc)은 Lucide 아이콘 + 사각형
const CHANNEL_ICON: Record<string, LucideIcon> = {
  license: FileText,
  etc: MoreHorizontal,
}
// 채널 브랜드 색 (플랫폼 마크 예외 — 채널명·강조 배지·아이콘 사각형에 사용)
const CHANNEL_COLOR: Record<string, string> = {
  youtube: '#FF0000',
  instagram: '#E1306C',
  tiktok: '#111111',
  license: '#5653FA',
  etc: '#352628',
}
const channelColor = (key: string) => CHANNEL_COLOR[key] ?? '#352628'
// 아이콘 사각형 배경색 (로고 없는 채널): License=브랜드 블루, 그 외=잉크
const iconBoxColor = (key: string) => (key === 'license' ? '#5653FA' : '#352628')
// 묶음 패키지 강조색 — 커머스는 핑크, 그 외 블루
const pkgAccent = (name: string) => (name.includes('커머스') ? '#FC8FFF' : '#5653FA')
// 액센트 위 콘텐츠 색: 핑크(#FC8FFF)는 밝아 잉크(LCMD 핑크+잉크), 블루는 화이트(블루+화이트)
const onPkgAccent = (name: string) => (name.includes('커머스') ? '#352628' : '#FFFFFF')
// 흰 배경 위 텍스트용 핑크: 밝은 #FC8FFF는 안 읽혀 pink-500(밝은 핑크 톤 유지·가독)로 (블루는 그대로)
const pkgAccentText = (name: string) => (name.includes('커머스') ? '#E574E8' : '#5653FA')

type EditState = {
  mode: 'add' | 'edit'
  channelKey: string
  id?: string
  type: string
  sub: string
  price: string
  optionsStr: string
  note: string
  desc: string
}

const splitOpts = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean)

type HubLine = { label: string; price: string }
type HubEditState = {
  mode: 'add' | 'edit'
  id?: string
  name: string
  items: string
  discountType: 'percent' | 'amount'
  discount: string // 값: percent 면 %, amount 면 원
  revenueShare: string
  notesStr: string
  lineItems: HubLine[]
}

// 개별 합계 = 라인 단가 합, 할인액 = %/원 적용, 통합가 = 합계 − 할인
const hubSubtotal = (m: HubEditState) => m.lineItems.reduce((s, l) => s + (num(l.price) ?? 0), 0)
const hubDiscountAmount = (m: HubEditState) => {
  const st = hubSubtotal(m)
  const v = num(m.discount) ?? 0
  return m.discountType === 'percent' ? Math.round((st * v) / 100) : v
}
const hubFinal = (m: HubEditState) => Math.max(0, hubSubtotal(m) - hubDiscountAmount(m))

// 문자열 → 원 단위 숫자 (빈값/비숫자는 undefined)
const num = (s: string): number | undefined => {
  const cleaned = s.replace(/[^0-9.-]/g, '')
  if (cleaned.trim() === '') return undefined
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : undefined
}

export function ProductsSection({ creatorId }: { creatorId?: string }) {
  if (creatorId) CREATOR_ID = creatorId // 셸 라우트 creator_id 반영(중첩 핸들러 공유)
  const [data, setData] = useState<RateCard | null>(null)
  const [openHub, setOpenHub] = useState<string | null>(null)
  const [modal, setModal] = useState<EditState | null>(null)
  const [confirm, setConfirm] = useState<EditState | null>(null)
  const [hubEdit, setHubEdit] = useState<HubEditState | null>(null)
  const [hubConfirm, setHubConfirm] = useState<HubEditState | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      let card = await fetchRateCard(CREATOR_ID).catch(() => null)
      // 단가표가 비어있으면 Salesforce 표준 단가에서 자동 seed (기본 소스=SF). seed 는 이미 있으면 skip.
      const isEmpty = !card || (card.channels.length === 0 && card.hubPackages.length === 0)
      if (isEmpty) {
        await seedRateCard(CREATOR_ID).catch(() => {})
        card = await fetchRateCard(CREATOR_ID).catch(() => card)
      }
      if (!cancelled) setData(card)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorId])

  function openAdd(channelKey: string) {
    setModal({ mode: 'add', channelKey, type: '', sub: '', price: '', optionsStr: '', note: '', desc: '' })
  }
  function openEdit(channelKey: string, p: RateProduct) {
    setModal({
      mode: 'edit',
      channelKey,
      id: p.id,
      type: p.type,
      sub: p.sub ?? '',
      price: p.price,
      optionsStr: p.options.join(', '),
      note: p.note ?? '',
      desc: p.desc ?? '',
    })
  }
  const setField = (f: keyof EditState, v: string) =>
    setModal((prev) => (prev ? { ...prev, [f]: v } : prev))

  // [검토 후 저장] → 필수값 확인 후 확인 단계로
  function review() {
    if (!modal || !modal.type.trim() || !modal.price.trim()) return
    setConfirm({ ...modal })
  }

  // [저장/수정 확정] → 낙관적 로컬 갱신 + API 호출(mock=no-op)
  function commit() {
    const m = confirm
    if (!m) return
    const product: RateProduct = {
      id: m.id ?? `tmp-${Date.now()}`,
      type: m.type.trim(),
      sub: m.sub.trim() || undefined,
      price: m.price.trim(),
      options: splitOpts(m.optionsStr),
      note: m.note.trim() || undefined,
      desc: m.desc.trim() || undefined,
    }
    setData((prev) => {
      if (!prev) return prev
      const channels = prev.channels.map((ch) => {
        if (ch.key !== m.channelKey) return ch
        if (m.mode === 'add') return { ...ch, products: [...ch.products, product] }
        return { ...ch, products: ch.products.map((p) => (p.id === m.id ? product : p)) }
      })
      return { ...prev, channels }
    })
    const input: RateProductInput & { id?: string } = {
      channelKey: m.channelKey as RateProductInput['channelKey'],
      id: m.id,
      type: product.type,
      sub: product.sub,
      price: product.price,
      options: product.options,
      note: product.note,
      desc: product.desc,
    }
    void saveRateProduct(CREATOR_ID, input).catch(() => {})
    setConfirm(null)
    setModal(null)
  }

  function removeProduct(channelKey: string, id: string) {
    setData((prev) =>
      prev
        ? {
            ...prev,
            channels: prev.channels.map((ch) =>
              ch.key === channelKey ? { ...ch, products: ch.products.filter((p) => p.id !== id) } : ch,
            ),
          }
        : prev,
    )
    void deleteRateProduct(CREATOR_ID, id).catch(() => {})
  }

  async function runSeed() {
    await seedRateCard(CREATOR_ID).catch(() => {})
    const fresh = await fetchRateCard(CREATOR_ID).catch(() => null)
    if (fresh) setData(fresh)
  }

  // ── 허브 묶음 편집 ──
  function openAddHub() {
    setHubEdit({
      mode: 'add', name: '', items: '', discountType: 'amount', discount: '',
      revenueShare: '', notesStr: '', lineItems: [{ label: '', price: '' }],
    })
  }
  function openEditHub(pk: HubPackage) {
    setHubEdit({
      mode: 'edit', id: pk.id, name: pk.name, items: pk.items ?? '',
      discountType: 'amount', // 저장은 금액으로 — 편집 시 금액 모드로 진입
      discount: pk.discount ? String(pk.discount) : '',
      revenueShare: pk.revenueShare ?? '', notesStr: pk.notes.join('\n'),
      lineItems: pk.lineItems.length
        ? pk.lineItems.map((li) => ({ label: li.label, price: li.price ? String(li.price) : '' }))
        : [{ label: '', price: '' }],
    })
  }
  // 채널별 단가에서 구성 라인으로 불러오기 (라벨 = "채널 · 상품", 단가 = 숫자 파싱)
  function addHubLineFromProduct(channelName: string, p: RateProduct) {
    const priceNum = num(p.price)
    setHubEdit((prev) =>
      prev
        ? {
            ...prev,
            lineItems: [
              ...prev.lineItems.filter((l) => l.label.trim() || l.price.trim()),
              { label: `${channelName} · ${p.type}`, price: priceNum != null ? String(priceNum) : '' },
            ],
          }
        : prev,
    )
  }
  const setHubField = (f: keyof HubEditState, v: string) =>
    setHubEdit((prev) => (prev ? { ...prev, [f]: v } : prev))
  const setHubLine = (i: number, f: keyof HubLine, v: string) =>
    setHubEdit((prev) =>
      prev ? { ...prev, lineItems: prev.lineItems.map((l, idx) => (idx === i ? { ...l, [f]: v } : l)) } : prev,
    )
  const addHubLine = () =>
    setHubEdit((prev) => (prev ? { ...prev, lineItems: [...prev.lineItems, { label: '', price: '' }] } : prev))
  const removeHubLine = (i: number) =>
    setHubEdit((prev) => (prev ? { ...prev, lineItems: prev.lineItems.filter((_, idx) => idx !== i) } : prev))

  function reviewHub() {
    if (!hubEdit || !hubEdit.name.trim() || hubFinal(hubEdit) <= 0) return
    setHubConfirm({ ...hubEdit })
  }
  function commitHub() {
    const m = hubConfirm
    if (!m) return
    const lineItems = m.lineItems
      .filter((l) => l.label.trim())
      .map((l) => ({ label: l.label.trim(), price: num(l.price) }))
    const notes = m.notesStr.split('\n').map((s) => s.trim()).filter(Boolean)
    const subtotalN = hubSubtotal(m)
    const discountN = hubDiscountAmount(m)
    const finalN = hubFinal(m)
    const pkg: HubPackage = {
      id: m.id ?? `tmp-${Date.now()}`,
      name: m.name.trim(),
      items: m.items.trim(),
      subtotal: subtotalN,
      discount: discountN,
      finalPrice: finalN,
      revenueShare: m.revenueShare.trim() || undefined,
      notes,
      lineItems: lineItems.map((li) => ({ label: li.label, price: li.price ?? 0 })),
    }
    setData((prev) =>
      prev
        ? {
            ...prev,
            hubPackages:
              m.mode === 'add'
                ? [...prev.hubPackages, pkg]
                : prev.hubPackages.map((p) => (p.id === m.id ? pkg : p)),
          }
        : prev,
    )
    void saveHubPackage(CREATOR_ID, {
      id: m.id,
      name: pkg.name,
      items: pkg.items || undefined,
      subtotal: subtotalN,
      discount: discountN,
      finalPrice: finalN,
      revenueShare: pkg.revenueShare,
      notes,
      lineItems,
    }).catch(() => {})
    setHubConfirm(null)
    setHubEdit(null)
  }
  function removeHub(id: string) {
    setData((prev) => (prev ? { ...prev, hubPackages: prev.hubPackages.filter((p) => p.id !== id) } : prev))
    void deleteHubPackage(CREATOR_ID, id).catch(() => {})
  }

  if (!data) return <div className="p-8 text-sm text-fg-3">단가표 불러오는 중…</div>

  const confirmChannelName = confirm
    ? data.channels.find((c) => c.key === confirm.channelKey)?.name ?? confirm.channelKey
    : ''
  const actionLabel = modal?.mode === 'edit' ? '수정' : '저장'

  return (
    // 캔버스(라벤더)는 셸에서 적용 · 카드는 솔리드 화이트 (그라데이션 미적용)
    <div className="space-y-8">
      {/* 헤더 */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-fg-3">Rate Card · 광고 단가표</div>
          <h2 className="mt-2 font-display text-2xl font-bold text-fg-1">Advertising Rate Card</h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={runSeed}
            className="inline-flex h-9 items-center gap-2 border border-line-soft px-3 text-xs font-semibold text-fg-2 hover:text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none"
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
            Salesforce에서 불러오기
          </button>
          <span className="text-xs text-fg-3">모든 단가 VAT 별도</span>
        </div>
      </div>

      {/* 허브 묶음 상품 (아코디언) */}
      <section className="border border-line-hairline bg-surface-base shadow-sm">
        <div className="flex items-center gap-2 bg-ink-800 px-4 py-3 text-fg-inverse">
          <span className="h-1.5 w-1.5 bg-brand-pink" aria-hidden />
          <span className="text-sm font-bold">HUB PROGRAM · 스페셜 패키지</span>
          <span className="ml-auto text-[10px] opacity-60">클릭 시 구성 상세</span>
          <button
            onClick={openAddHub}
            className="ml-2 inline-flex flex-none items-center gap-1 whitespace-nowrap border border-ink-600 px-2 py-1 text-[11px] font-semibold text-fg-inverse hover:bg-ink-900 focus-visible:outline-none"
          >
            <Plus className="h-3 w-3" aria-hidden />패키지
          </button>
        </div>
        {data.hubPackages.map((pk) => {
          const open = openHub === pk.id
          // seed엔 finalPrice만 있을 수 있어 개별합계/할인은 라인 합계로 폴백 계산(핸드오프 요약)
          const lineSum = pk.lineItems.reduce((s, li) => s + (li.price || 0), 0)
          const subtotalV = pk.subtotal || lineSum
          const discountV = pk.discount || Math.max(0, subtotalV - (pk.finalPrice || 0))
          // 구성 요약(items)이 비면 라인아이템 라벨로 자동 폴백 (예: "YouTube 롱폼 + Instagram 릴스")
          const composition = (pk.items && pk.items.trim()) || pk.lineItems.map((li) => li.label).join(' + ')
          const accent = pkgAccent(pk.name) // 커머스 패키지 = 핑크, 그 외 = 블루 (전체 통일)
          const onAccent = onPkgAccent(pk.name) // 핑크 위엔 잉크, 블루 위엔 화이트
          const accentText = pkgAccentText(pk.name) // 흰 배경 위 텍스트용(읽히는 진한 핑크)
          return (
            <div key={pk.id} className="border-t border-line-soft first:border-t-0">
              <div className="flex items-center gap-2 pr-3">
                <button
                  onClick={() => setOpenHub(open ? null : pk.id)}
                  className="flex min-w-0 flex-1 items-center gap-3 px-4 py-4 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 flex-none" style={{ background: pkgAccent(pk.name) }} aria-hidden />
                      <span className="text-base font-bold text-fg-1">{pk.name}</span>
                    </div>
                    {composition && <div className="mt-1 pl-4 text-xs text-fg-2">{composition}</div>}
                  </div>
                  <div className="flex-none text-right font-display text-base font-bold tabular-nums text-fg-1">
                    {pk.finalPrice ? won(pk.finalPrice) : pk.name}
                  </div>
                </button>
                <button
                  onClick={() => openEditHub(pk)}
                  title="편집"
                  className="flex h-7 w-7 flex-none items-center justify-center border border-line-soft text-fg-3 hover:text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none"
                >
                  <Pencil className="h-3.5 w-3.5" aria-hidden />
                </button>
                <button
                  onClick={() => setOpenHub(open ? null : pk.id)}
                  aria-label={open ? '구성 상세 접기' : '구성 상세 펼치기'}
                  className="flex h-6 w-6 flex-none items-center justify-center focus-visible:outline-none"
                  style={{ color: pkgAccent(pk.name) }}
                >
                  {open ? <Minus className="h-5 w-5" aria-hidden /> : <Plus className="h-5 w-5" aria-hidden />}
                </button>
              </div>

              {open && (
                <div className="bg-surface-soft px-6 pb-6 pt-5">
                  <div className="mb-3 text-xs font-bold uppercase tracking-wide text-fg-3">구성 상세 · 개별 단가</div>
                  <ul>
                    {pk.lineItems.map((li) => (
                      <li
                        key={li.label}
                        className="flex items-center justify-between gap-3 border-b border-line-soft py-2.5 text-sm"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="flex h-4 w-4 flex-none items-center justify-center rounded-full" style={{ background: accent }}>
                            <Check className="h-2.5 w-2.5 text-fg-inverse" aria-hidden />
                          </span>
                          <span className="truncate text-fg-1">{li.label}</span>
                        </span>
                        <span className="flex-none font-display font-semibold tabular-nums text-fg-1">{li.price > 0 ? won(li.price) : '포함'}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 border border-line-soft bg-surface-base p-4">
                    <div className="flex items-center justify-between pb-2 text-sm text-fg-3">
                      <span>개별 합계</span>
                      <span className="font-display tabular-nums line-through">{subtotalV > 0 ? won(subtotalV) : '—'}</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-line-soft pb-2 text-sm font-semibold" style={{ color: accentText }}>
                      <span>패키지 통합 할인</span>
                      <span className="font-display tabular-nums">{discountV > 0 ? '− ' + won(discountV) : '—'}</span>
                    </div>
                    <div className="mt-3 flex items-baseline justify-between">
                      <span className="text-sm font-bold text-fg-1">통합 패키지가</span>
                      <span className="font-display text-xl font-bold tabular-nums text-fg-1">
                        {pk.finalPrice ? won(pk.finalPrice) : '—'}
                      </span>
                    </div>
                    <div className="mt-1 text-right text-[10px] text-fg-3">VAT 별도</div>
                    {pk.revenueShare && (
                      <div className="mt-3 flex items-center gap-2 px-2.5 py-1.5" style={{ background: accent + '1A' }}>
                        <span className="px-1.5 py-0.5 text-[9px] font-bold" style={{ background: accent, color: onAccent }}>R/S</span>
                        <span className="text-xs text-fg-3">수익배분</span>
                        <span className="ml-auto text-sm font-semibold text-fg-1">{pk.revenueShare.replace(/\s*\(R\/S\)\s*$/i, '')}</span>
                      </div>
                    )}
                  </div>
                  {pk.notes.length > 0 && (
                    <>
                      <div className="mb-2 mt-5 text-xs font-bold uppercase tracking-wide text-fg-3">유의사항</div>
                      <ul className="space-y-1">
                        {pk.notes.map((n) => (
                          <li key={n} className="flex items-start gap-2 text-sm text-fg-2">
                            <span className="mt-2 h-1 w-1 flex-none bg-fg-3" aria-hidden />
                            {n}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </section>

      {/* 채널별 단가 */}
      <section>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-fg-1">채널별 단가</h3>
          <span className="text-xs text-fg-3">모든 단가 VAT 별도</span>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {data.channels.map((ch) => {
            const logo = CHANNEL_LOGO[ch.key]
            const Icon = CHANNEL_ICON[ch.key] ?? MoreHorizontal
            return (
            <div key={ch.key} className="border border-line-hairline bg-surface-base shadow-sm">
              <div className="flex items-center gap-3 border-b border-line-hairline px-4 py-3">
                {logo ? (
                  <img src={logo} alt="" aria-hidden className="h-8 w-8 flex-none" />
                ) : (
                  <span className="flex h-8 w-8 flex-none items-center justify-center rounded-[7px] text-fg-inverse" style={{ background: iconBoxColor(ch.key) }}>
                    <Icon className="h-4 w-4" aria-hidden />
                  </span>
                )}
                <span className="text-base font-bold" style={{ color: channelColor(ch.key) }}>{ch.name}</span>
                <span className="ml-auto whitespace-nowrap text-xs text-fg-3">{ch.products.length}개 상품</span>
                <button
                  onClick={() => openAdd(ch.key)}
                  className="inline-flex flex-none items-center gap-1 whitespace-nowrap border border-line-soft px-2 py-1 text-xs font-semibold hover:bg-surface-soft focus-visible:border-brand-blue focus-visible:outline-none"
                  style={{ color: channelColor(ch.key) }}
                >
                  <Plus className="h-3 w-3" aria-hidden />상품
                </button>
              </div>
              {ch.products.map((p) => (
                <div key={p.id} className="border-t border-line-hairline px-4 py-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-base font-bold text-fg-1">{p.type}</div>
                      {p.sub && <div className="mt-1 text-xs text-fg-3">{p.sub}</div>}
                    </div>
                    <div className="flex flex-none items-center gap-2">
                      <span className="font-display text-base font-bold tabular-nums text-fg-1">{p.price}</span>
                      <button
                        onClick={() => openEdit(ch.key, p)}
                        title="편집"
                        className="flex h-7 w-7 items-center justify-center border border-line-soft text-fg-3 hover:text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none"
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    </div>
                  </div>
                  {(p.options.length > 0 || p.note) && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {p.options.map((o) => (
                        <span key={o} className="bg-surface-soft px-2 py-0.5 text-[11px] text-fg-2">
                          {o}
                        </span>
                      ))}
                      {p.note && (
                        <span className="px-2 py-0.5 text-[11px] font-semibold" style={{ background: channelColor(ch.key) + '1A', color: channelColor(ch.key) }}>
                          {p.note}
                        </span>
                      )}
                    </div>
                  )}
                  {p.desc && <p className="mt-2 text-sm leading-relaxed text-fg-2">{p.desc}</p>}
                </div>
              ))}
            </div>
          )})}
        </div>
      </section>

      {/* ── 편집 모달 ── */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-[color:var(--bg-overlay)]" onClick={() => setModal(null)} aria-hidden />
          <div className="relative flex max-h-[88vh] w-full max-w-lg flex-col overflow-y-auto border border-line-soft bg-surface-base shadow-xl">
            <div className="flex items-center justify-between border-b border-line-soft px-5 py-4">
              <div>
                <div className="text-lg font-bold text-fg-1">{modal.mode === 'add' ? '상품 추가' : '상품 수정'}</div>
                <div className="mt-0.5 text-xs text-fg-3">
                  {data.channels.find((c) => c.key === modal.channelKey)?.name ?? modal.channelKey} 채널
                </div>
              </div>
              <button
                onClick={() => setModal(null)}
                className="flex h-8 w-8 items-center justify-center text-fg-3 hover:text-fg-1 focus-visible:outline-none"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <div className="flex flex-col gap-4 px-5 py-5">
              <Field label="상품 유형" required value={modal.type} onChange={(v) => setField('type', v)} placeholder="예: 브랜디드 통합 영상" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="부제" value={modal.sub} onChange={(v) => setField('sub', v)} placeholder="예: 롱폼 · 8분 이상" />
                <Field label="단가" required value={modal.price} onChange={(v) => setField('price', v)} placeholder="예: ₩12,000,000 / 별도 협의" />
              </div>
              <Field label="옵션 · 쉼표(,)로 구분" value={modal.optionsStr} onChange={(v) => setField('optionsStr', v)} placeholder="예: 전용 기획·편집 포함, 2차 활용 +30%" />
              <Field label="특이사항 태그 · 강조 배지" value={modal.note} onChange={(v) => setField('note', v)} placeholder="예: 최고 도달·설득력" />
              <div>
                <div className="mb-1.5 text-xs font-semibold text-fg-2">부연 설명</div>
                <textarea
                  value={modal.desc}
                  onChange={(e) => setField('desc', e.target.value)}
                  placeholder="상품에 대한 추가 설명"
                  className="min-h-[70px] w-full resize-y rounded border border-line-soft bg-surface-base px-3 py-2 text-sm text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-line-soft bg-surface-soft px-5 py-4">
              <span className="text-xs text-fg-3">
                <span className="text-brand-pink">*</span> 필수 항목
              </span>
              <div className="flex gap-2">
                {modal.mode === 'edit' && modal.id && (
                  <button
                    onClick={() => {
                      removeProduct(modal.channelKey, modal.id!)
                      setModal(null)
                    }}
                    className="h-10 border border-line-soft px-4 text-sm font-medium text-fg-2 hover:text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none"
                  >
                    삭제
                  </button>
                )}
                <button
                  onClick={() => setModal(null)}
                  className="h-10 border border-line-soft px-4 text-sm font-medium text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none"
                >
                  취소
                </button>
                <button
                  onClick={review}
                  disabled={!modal.type.trim() || !modal.price.trim()}
                  className="h-10 bg-brand-blue px-5 text-sm font-semibold text-fg-inverse disabled:opacity-40 focus-visible:outline-none"
                >
                  검토 후 저장
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 2단 확인 모달 ── */}
      {confirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-[color:var(--bg-overlay)]" aria-hidden />
          <div className="relative w-full max-w-md border border-line-soft bg-surface-base shadow-xl">
            <div className="flex items-start gap-3 px-6 pb-1 pt-6">
              <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[rgba(86,83,250,0.1)]">
                <Info className="h-5 w-5 text-brand-blue" aria-hidden />
              </span>
              <div>
                <div className="text-base font-bold text-fg-1">이대로 {actionLabel}할까요?</div>
                <p className="mt-1 text-sm leading-relaxed text-fg-2">
                  단가표는 원천 데이터로 쓰입니다. 아래 내용을 다시 한번 확인해 주세요.
                </p>
              </div>
            </div>
            <div className="mx-6 my-4 border border-line-soft">
              <div className="flex items-baseline justify-between gap-3 border-b border-line-soft px-4 py-3">
                <div className="min-w-0">
                  <div className="text-[10px] text-fg-3">{confirmChannelName} · 상품</div>
                  <div className="mt-0.5 text-base font-bold text-fg-1">{confirm.type}</div>
                  {confirm.sub && <div className="mt-0.5 text-xs text-fg-3">{confirm.sub}</div>}
                </div>
                <div className="flex-none text-right font-display text-base font-bold tabular-nums text-brand-blue">{confirm.price}</div>
              </div>
              {splitOpts(confirm.optionsStr).length > 0 && (
                <div className="flex flex-wrap gap-1.5 px-4 py-3">
                  {splitOpts(confirm.optionsStr).map((o) => (
                    <span key={o} className="bg-surface-soft px-2 py-0.5 text-[11px] text-fg-2">
                      {o}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2 px-6 pb-6 pt-1">
              <button
                onClick={() => setConfirm(null)}
                className="h-11 flex-1 border border-line-soft text-sm font-semibold text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none"
              >
                다시 검토
              </button>
              <button
                onClick={commit}
                className="h-11 flex-1 bg-brand-blue text-sm font-bold text-fg-inverse focus-visible:outline-none"
              >
                {actionLabel} 확정
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 허브 묶음 편집 모달 ── */}
      {hubEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-[color:var(--bg-overlay)]" onClick={() => setHubEdit(null)} aria-hidden />
          <div className="relative flex max-h-[88vh] w-full max-w-xl flex-col overflow-y-auto border border-line-soft bg-surface-base shadow-xl">
            <div className="flex items-center justify-between border-b border-line-soft px-5 py-4">
              <div className="text-lg font-bold text-fg-1">{hubEdit.mode === 'add' ? '묶음 패키지 추가' : '묶음 패키지 수정'}</div>
              <button
                onClick={() => setHubEdit(null)}
                className="flex h-8 w-8 items-center justify-center text-fg-3 hover:text-fg-1 focus-visible:outline-none"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <div className="flex flex-col gap-4 px-5 py-5">
              <Field label="패키지명" required value={hubEdit.name} onChange={(v) => setHubField('name', v)} placeholder="예: 멀티채널 런칭 패키지" />
              <Field label="구성 요약" value={hubEdit.items} onChange={(v) => setHubField('items', v)} placeholder="예: YouTube 롱폼 + Instagram 릴스 + TikTok 숏폼" />

              {/* 구성 상세 라인 — 채널 단가에서 불러오거나 직접 추가 */}
              <div>
                <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-fg-2">구성 상세 · 개별 단가</span>
                  <div className="flex items-center gap-2">
                    <select
                      value=""
                      onChange={(e) => {
                        const [ci, pi] = e.target.value.split('-').map(Number)
                        const ch = data?.channels[ci]
                        const p = ch?.products[pi]
                        if (ch && p) addHubLineFromProduct(ch.name, p)
                      }}
                      className="h-8 rounded border border-line-soft bg-surface-base px-2 text-xs text-fg-2 focus-visible:border-brand-blue focus-visible:outline-none"
                    >
                      <option value="">＋ 채널 단가 불러오기…</option>
                      {data?.channels.map((ch, ci) =>
                        ch.products.map((p, pi) => (
                          <option key={`${ci}-${pi}`} value={`${ci}-${pi}`}>
                            {ch.name} · {p.type} ({p.price})
                          </option>
                        )),
                      )}
                    </select>
                    <button onClick={addHubLine} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-blue focus-visible:outline-none">
                      <Plus className="h-3 w-3" aria-hidden />직접 추가
                    </button>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {hubEdit.lineItems.map((li, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        value={li.label}
                        onChange={(e) => setHubLine(i, 'label', e.target.value)}
                        placeholder="구성 항목"
                        className="h-9 min-w-0 flex-1 rounded border border-line-soft bg-surface-base px-3 text-sm text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none"
                      />
                      <input
                        value={li.price}
                        onChange={(e) => setHubLine(i, 'price', e.target.value)}
                        placeholder="원(숫자)"
                        className="h-9 w-32 flex-none rounded border border-line-soft bg-surface-base px-3 text-right text-sm tabular-nums text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none"
                      />
                      <button
                        onClick={() => removeHubLine(i)}
                        aria-label="라인 삭제"
                        className="flex h-9 w-9 flex-none items-center justify-center border border-line-soft text-fg-3 hover:text-fg-1 focus-visible:outline-none"
                      >
                        <X className="h-4 w-4" aria-hidden />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* 자동 합계 · 할인 · 통합가 */}
              <div className="space-y-2.5 border border-line-soft bg-surface-soft p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-fg-3">개별 합계 <span className="text-[10px]">(자동)</span></span>
                  <span className="font-semibold font-display tabular-nums text-fg-1">{won(hubSubtotal(hubEdit))}</span>
                </div>
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="flex-none text-fg-3">통합 할인</span>
                  <div className="flex items-center gap-1.5">
                    <div className="flex border border-line-soft">
                      {(['amount', 'percent'] as const).map((t) => (
                        <button
                          key={t}
                          onClick={() => setHubEdit((prev) => (prev ? { ...prev, discountType: t } : prev))}
                          className={
                            'h-8 w-8 text-xs font-semibold focus-visible:outline-none ' +
                            (hubEdit.discountType === t ? 'bg-brand-blue text-fg-inverse' : 'bg-surface-base text-fg-3')
                          }
                        >
                          {t === 'amount' ? '₩' : '%'}
                        </button>
                      ))}
                    </div>
                    <input
                      value={hubEdit.discount}
                      onChange={(e) => setHubField('discount', e.target.value)}
                      placeholder={hubEdit.discountType === 'percent' ? '10' : '3500000'}
                      className="h-8 w-28 rounded border border-line-soft bg-surface-base px-3 text-right text-sm tabular-nums text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none"
                    />
                    <span className="w-28 flex-none text-right text-xs font-display tabular-nums text-fg-2">− {won(hubDiscountAmount(hubEdit))}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between border-t border-line-soft pt-2.5">
                  <span className="text-sm font-semibold text-fg-1">통합 패키지가</span>
                  <span className="font-display text-lg font-bold tabular-nums text-fg-1">
                    {won(hubFinal(hubEdit))} <span className="text-[10px] font-normal text-fg-3">VAT 별도</span>
                  </span>
                </div>
              </div>

              <Field label="수익배분 R/S" value={hubEdit.revenueShare} onChange={(v) => setHubField('revenueShare', v)} placeholder="예: 판매액의 10% (R/S)" />

              <div>
                <div className="mb-1.5 text-xs font-semibold text-fg-2">유의사항 · 줄바꿈으로 구분</div>
                <textarea
                  value={hubEdit.notesStr}
                  onChange={(e) => setHubField('notesStr', e.target.value)}
                  placeholder={'3개 채널 동시 집행 기준\n2차 활용은 별도 협의'}
                  className="min-h-[70px] w-full resize-y rounded border border-line-soft bg-surface-base px-3 py-2 text-sm text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-line-soft bg-surface-soft px-5 py-4">
              <span className="text-xs text-fg-3"><span className="text-brand-pink">*</span> 필수 항목</span>
              <div className="flex gap-2">
                {hubEdit.mode === 'edit' && hubEdit.id && (
                  <button
                    onClick={() => {
                      removeHub(hubEdit.id!)
                      setHubEdit(null)
                    }}
                    className="h-10 border border-line-soft px-4 text-sm font-medium text-fg-2 hover:text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none"
                  >
                    삭제
                  </button>
                )}
                <button
                  onClick={() => setHubEdit(null)}
                  className="h-10 border border-line-soft px-4 text-sm font-medium text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none"
                >
                  취소
                </button>
                <button
                  onClick={reviewHub}
                  disabled={!hubEdit.name.trim() || hubFinal(hubEdit) <= 0}
                  className="h-10 bg-brand-blue px-5 text-sm font-semibold text-fg-inverse disabled:opacity-40 focus-visible:outline-none"
                >
                  검토 후 저장
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── 허브 2단 확인 ── */}
      {hubConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
          <div className="absolute inset-0 bg-[color:var(--bg-overlay)]" aria-hidden />
          <div className="relative w-full max-w-md border border-line-soft bg-surface-base shadow-xl">
            <div className="flex items-start gap-3 px-6 pb-1 pt-6">
              <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[rgba(86,83,250,0.1)]">
                <Info className="h-5 w-5 text-brand-blue" aria-hidden />
              </span>
              <div>
                <div className="text-base font-bold text-fg-1">이대로 {hubConfirm.mode === 'edit' ? '수정' : '저장'}할까요?</div>
                <p className="mt-1 text-sm leading-relaxed text-fg-2">
                  단가표는 원천 데이터로 쓰입니다. 아래 내용을 다시 한번 확인해 주세요.
                </p>
              </div>
            </div>
            <div className="mx-6 my-4 border border-line-soft px-4 py-3">
              <div className="flex items-baseline justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] text-fg-3">묶음 패키지</div>
                  <div className="mt-0.5 text-base font-bold text-fg-1">{hubConfirm.name}</div>
                  {hubConfirm.items.trim() && <div className="mt-0.5 text-xs text-fg-3">{hubConfirm.items}</div>}
                </div>
                <div className="flex-none text-right font-display text-base font-bold tabular-nums text-brand-blue">
                  {won(hubFinal(hubConfirm))}
                </div>
              </div>
              <div className="mt-2 text-xs text-fg-3">
                구성 {hubConfirm.lineItems.filter((l) => l.label.trim()).length}개
                {hubConfirm.revenueShare.trim() ? ` · R/S ${hubConfirm.revenueShare.trim()}` : ''}
              </div>
            </div>
            <div className="flex gap-2 px-6 pb-6 pt-1">
              <button
                onClick={() => setHubConfirm(null)}
                className="h-11 flex-1 border border-line-soft text-sm font-semibold text-fg-1 focus-visible:border-brand-blue focus-visible:outline-none"
              >
                다시 검토
              </button>
              <button
                onClick={commitHub}
                className="h-11 flex-1 bg-brand-blue text-sm font-bold text-fg-inverse focus-visible:outline-none"
              >
                {hubConfirm.mode === 'edit' ? '수정' : '저장'} 확정
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  required?: boolean
}) {
  return (
    <div>
      <div className="mb-1.5 text-xs font-semibold text-fg-2">
        {label} {required && <span className="text-brand-pink">*</span>}
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-10 w-full rounded border border-line-soft bg-surface-base px-3 text-sm text-fg-1 placeholder:text-fg-3 focus-visible:border-brand-blue focus-visible:outline-none"
      />
    </div>
  )
}
